
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Maximize Link conversion and customer confidence with pre and post Link messaging'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Link messaging' subtitle='Shape your messaging around Link to optimize conversion and user experience' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Factors that you control, such as how Link is placed in your onboarding flow, and the UI of the page where it is hosted, can have large impacts on Link conversion. This article outlines best practices for Link presentation that you can adopt to maximize conversion and overall user experience.`}</p>
    <h4 {...{
      "id": "pre-link-messaging-best-practices"
    }}>{`Pre-Link messaging best practices`}</h4>
    <h5 {...{
      "id": "explain-the-benefit-to-the-user-in-direct-concise-language"
    }}>{`Explain the benefit to the user, in direct, concise language`}</h5>
    <p>{`Your UI should tell the user why they want to use Link. For example, linking an account might let them make free transfers, versus having to pay a credit card transaction fee. Or it might be faster, allowing them to avoid manual data entry of balances or transactions, or having to wait several days for a micro-deposit-based verification flow.`}</p>
    <p>{`When explaining the benefits, use concise explanations, rather than displaying large blocks of text. If you need to provide more details to fully explain something, you can use a "learn more" link to display a modal with additional information. `}</p>
    <p>{`When providing value props, two bullets is best. Only use more than two if the additional bullets convey financial benefits, such as "Access funds instantly," "Get the best available rate", or "No fees". For best uptake, consider putting the strongest value prop in the header if it is compelling, e.g. "Add a bank account to move money with no fees."`}</p>
    <p>{`In the Link CTA, "add a bank account" is preferable to "connect a bank account" or "link a bank account" for most use cases. "Add instantly" is a strong example CTA -- the word "instantly" drives user uptake.`}</p>
    <h5 {...{
      "id": "tell-the-user-what-to-expect-including-what-data-is-shared-and-why"
    }}>{`Tell the user what to expect, including what data is shared and why`}</h5>
    <p>{`Before launching the Link flow, explain to the user that they'll be prompted to link a bank account with Plaid. Explain what data your app will be collecting from their account, and why it's needed. Because long blocks of text can reduce Link uptake, the best approach is to provide a concise, user-friendly explanation of how your app uses data from Plaid, then link to your Privacy Policy or other documents if needed for a more comprehensive explanation. `}</p>
    <h5 {...{
      "id": "present-link-as-the-default"
    }}>{`Present Link as the default`}</h5>
    <p>{`Rather than presenting Link and manual flows as equal alternatives, encourage your customers to use Link through the size, positioning, and color of the Link entry point. You can also use labels such as "Recommended" or "Preferred". Yet another option is to display manual flows as an option only after the `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callback (indicating a failed link attempt) has been received.`}</p>
    <h5 {...{
      "id": "explain-that-link-is-secure"
    }}>{`Explain that Link is secure`}</h5>
    <p>{`Let customers know that Link is secure and uses 256-bit encryption; a lock icon can also be used to help convey that Link uses encryption. Explain that your app does not have access to their credentials. If your app implements the best practice of allowing users to `}<a parentName="p" {...{
        "href": "#allow-users-to-manage-linked-items"
      }}>{`disconnect accounts from within the app`}</a>{`, explaining that they can choose to unlink their account later can increase users' confidence in their control over their data. `}</p>
    <h5 {...{
      "id": "do-not-use-plaid-in-your-link-url"
    }}>{`Do not use Plaid in your Link URL`}</h5>
    <p>{`Using the string "Plaid" in the URL of the page from which Link is launched, especially in the subdomain (for example, `}<a parentName="p" {...{
        "href": "https://plaid.wonderwallet.com"
      }}>{`https://plaid.wonderwallet.com`}</a>{`) may cause customer confusion regarding the boundaries between your app and Plaid. `}</p>
    <h5 {...{
      "id": "provide-a-polished-engaging-flow"
    }}>{`Provide a polished, engaging flow`}</h5>
    <p>{`A Link hosting flow that is engaging, polished, and reflects your brand conveys the legitimacy and importance of linking an account. Illustrations and interactive elements can all be a part of your pre-Link messaging.`}</p>
    <h5 {...{
      "id": "use-multiple-channels-to-engage-users"
    }}>{`Use multiple channels to engage users`}</h5>
    <p>{`If a user does not link an account during their first interaction with your app, you can use other channels such as email, text, or mobile notifications to remind them. These channels should use the same best practices as your in-app UI. You can also use these channels to notify users when they need to take action to repair a linked account via `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`.`}</p>
    <h5 {...{
      "id": "if-possible-use-embedded-institution-search"
    }}>{`If possible, use Embedded Institution Search`}</h5>
    <p><a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search/"
      }}>{`Embedded Institution Search`}</a>{` has been shown to increase the percentage of users who link an account with Plaid. `}</p>
    <h4 {...{
      "id": "pre-link-messaging-examples"
    }}>{`Pre-Link messaging examples`}</h4>
    <p>{`Below are some examples of fictional Plaid-powered apps that incorporate elements of best-practice messaging. While every app will have its own unique pre-Link UI, these examples are intended to provide inspiration and demonstrate how the principles above can be applied.`}</p>
    <h5 {...{
      "id": "payments-example"
    }}>{`Payments example`}</h5>
    <Image src='/assets/img/docs/link-best-practices/pre-link-payments.png' alt="Settings page showing payment methods and Plaid bank verification options with instant and secure account linking features." position='center' expandable={true} mdxType="Image" />
    <p>{`This pre-Link flow conveys the benefit to the user, presents Link as the default experience, and uses social proof to show that Link is a secure solution trusted by many users. It mentions Plaid by name before launching the Link flow, helping the user understand that Plaid is trusted by the app. It also clearly explains what customer-permissioned data the app will be using, how that data will be used, and that the user can revoke access to that data.`}</p>
    <h5 {...{
      "id": "lending-examples"
    }}>{`Lending examples`}</h5>
    <Image src='/assets/img/docs/link-best-practices/pre-link-embedded-credit.png' width='350' position='center' mdxType="Image" />
    <p>{`This pane uses `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search"
      }}>{`Embedded Institution Search`}</a>{` to increase uptake and uses direct language like "Add a bank account".`}</p>
    <Image src='/assets/img/docs/link-best-practices/pre-link-credit.png' alt="Two mobile screens showing loan application steps: Left screen offers income verification via 'Connect Bank' button; right screen highlights benefits of linking bank for faster approval, better rates, and fund access." position='center' mdxType="Image" />
    <p>{`These pre-Link panes convey the benefit to the user of linking an account, present Link as the default experience, and explain that Link is secure.`}</p>
    <Image src='/assets/img/docs/link-best-practices/pre-link-credit-standalone.png' width='350' position='center' mdxType="Image" />
    <p>{`This pane puts a strong financial benefit to the end user in the headline ("You may get a higher approved loan amount"). It uses three bullets, one of which is a strong financial benefit to the user (expedited funds access), which justifies the inclusion of a third bullet. It also concisely makes it clear that Plaid is secure, uses direct "add a bank account" language, and positions Plaid as the default. `}</p>
    <h5 {...{
      "id": "personal-financial-management-example"
    }}>{`Personal financial management example`}</h5>
    <Image src='/assets/img/docs/link-best-practices/pre-link-budgeting.png' alt="Mobile app screens showing Neo Budget features: overdraft protection, pay advances, budget tracker, data safety with AES-256 encryption." position='center' mdxType="Image" />
    <p>{`This pre-Link pane conveys the benefit to the user, explains that Link is secure, and contains a link to additional security messaging with more detail on security practices, including an explanation of how the user can manage their linked account.`}</p>
    <h5 {...{
      "id": "email-messaging-example"
    }}>{`Email messaging example`}</h5>
    <Image src='/assets/img/docs/link-best-practices/email-link-reminder.png' alt="Email from Neo Budget prompting user to connect bank via Plaid for features like overdraft alerts, pay advances, and budget tracking." position='center' mdxType="Image" />
    <p>{`This follow-up email, sent when a user does not link an account within 24 hours of signing up for the app, uses the same branding as the main app, contains a clear call to action, and explains the benefits of Link and why the user should link their account.`}</p>
    <h4 {...{
      "id": "post-link-messaging-best-practices"
    }}>{`Post-Link messaging best practices`}</h4>
    <p>{`While post-Link messaging has a less direct impact on conversion, it can still impact the confidence your users have in Plaid and in your app.`}</p>
    <h5 {...{
      "id": "present-a-success-screen"
    }}>{`Present a success screen`}</h5>
    <p>{`Let the user know that they successfully linked their account to your app. You can also show some of the data that your app has collected, so that the user can have proof that the link process worked correctly. If applicable, have a CTA on this screen that allows them to link another account. `}</p>
    <h5 {...{
      "id": "allow-users-to-manage-linked-items"
    }}>{`Allow users to manage linked Items`}</h5>
    <p>{`Having an area within your app where users can view, manage, or remove linked Items is a privacy and security best practice. It can also minimize the risk of `}<a parentName="p" {...{
        "href": "/docs/link/duplicate-items"
      }}>{`duplicate Items`}</a>{` (when a user accidentally adds the same Item twice) and help users fix Items that are unhealthy and need to go through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`.`}</p>
    <h4 {...{
      "id": "post-link-messaging-example"
    }}>{`Post-Link messaging example`}</h4>
    <Image src='/assets/img/docs/link-best-practices/success-page.png' alt="Account ending in **3345 successfully connected. Option to connect another account for better rates with 'Connect Another' button." width='350' position='center' mdxType="Image" />
    <p>{`This post-Link pane includes a success screen to instill confidence. It incorporates the account mask, which lets the user know their account data was retrieved, and incorporates a call to action to link another account.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To measure the impact of these steps or set up an experiment to evaluate them, see `}<a parentName="p" {...{
        "href": "/docs/link/measuring-conversion"
      }}>{`Measuring Link conversion`}</a>{`. For more conversion maximization tips, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices"
      }}>{`Optimizing Link conversion`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "pre-link-messaging-best-practices",
        level: 4,
        title: "Pre-Link messaging best practices",
      },
{
        id: "pre-link-messaging-examples",
        level: 4,
        title: "Pre-Link messaging examples",
      },
{
        id: "post-link-messaging-best-practices",
        level: 4,
        title: "Post-Link messaging best practices",
      },
{
        id: "post-link-messaging-example",
        level: 4,
        title: "Post-Link messaging example",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
